import { useState } from "react";
import { toast } from "sonner";
import { Download, Gauge, Package, Send, Loader2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCachedGet } from "@/lib/cache";
import { STATE_META, fmtDate } from "./licenseUtil";

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-[color:var(--bd-sub)] bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-display font-semibold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Breakdown({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="tp-card p-5" data-testid={testid}>
      <div className="font-display font-semibold mb-3">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-slate-500">No interviews counted yet this month.</div> : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.label} className="text-sm">
              <div className="flex justify-between gap-3"><span className="truncate text-slate-700">{r.label}</span><span className="font-semibold tabular-nums">{r.count}</span></div>
              <div className="h-1.5 rounded-full bg-slate-100 mt-1"><div className="h-full rounded-full bg-indigo-400" style={{ width: `${(100 * r.count) / max}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestMore({ plan }) {
  const { user, tenant } = useAuth();
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/assistant/ticket", { name: user.name || user.email, email: user.email, company: tenant?.name || "", topic: "upgrade",
        message: `Please add more interviews or upgrade our plan (currently ${plan}).` });
      toast.success("Request sent — our team will contact you shortly");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={send} disabled={busy} data-testid="license-request-more"
      className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 transition-colors">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request more interviews
    </button>
  );
}

export default function PlanUsage() {
  const [s] = useCachedGet("/license");
  if (!s) return <div className="text-sm text-slate-500">Loading plan…</div>;
  const meta = STATE_META[s.state] || STATE_META.ok;
  const pct = s.unlimited ? 0 : Math.min(100, s.pct || 0);
  const maxHist = Math.max(1, ...s.history.map((h) => h.count));
  const download = async () => {
    const r = await api.get("/license/usage.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement("a"), { href: url, download: `interview-usage-${s.period}.csv` }).click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="plan-usage">
      <div className="tp-card p-6">
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center"><Gauge className="w-5 h-5" /></div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-display text-xl font-semibold" data-testid="license-plan-name">{s.plan.name} plan</h3>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid="license-state">{meta.label}</span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {s.unlimited ? "Unlimited interviews · usage is tracked for reporting." :
                s.plan.key === "trial" ? `Trial ends ${fmtDate(s.trial_ends_at)}` : `Allowance resets ${fmtDate(s.renews_on)}`}
              {s.overage_price != null && !s.unlimited && s.grace > 0 ? ` · extra interviews $${s.overage_price} each` : ""}
            </p>
          </div>
          <RequestMore plan={s.plan.name} />
        </div>
        <div className="mt-6">
          <div className="flex items-baseline justify-between text-sm mb-2">
            <span className="text-slate-600"><b className="text-slate-900 text-lg tabular-nums" data-testid="license-used">{s.used}</b>{s.unlimited ? " interviews this month" : ` of ${s.allowance} included interviews used`}</span>
            {!s.unlimited && <span className="text-slate-500 tabular-nums" data-testid="license-pct">{s.pct}%</span>}
          </div>
          {!s.unlimited && (
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden relative" data-testid="license-bar">
              <div className={`h-full ${meta.bar} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
              <div className="absolute inset-y-0 w-px bg-slate-400/60" style={{ left: "80%" }} title="80% alert" />
            </div>
          )}
          <p className="text-xs text-slate-500 mt-2">A candidate counts once, when they start their first round. Invites that are never started don't count.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Included left" value={s.unlimited ? "∞" : s.allowance_left} testid="license-stat-included" />
        <Stat label="Credits left" value={s.credits_left} sub={s.from_credits ? `${s.from_credits} used this month` : null} testid="license-stat-credits" />
        <Stat label="Grace" value={s.unlimited ? "—" : `${Math.max(0, s.grace - s.overage)} / ${s.grace}`} sub={s.unlimited ? null : `${s.grace_pct}% of allowance`} testid="license-stat-grace" />
        <Stat label="Extra interviews" value={s.overage} sub={s.billable_overage ? `$${s.billable_overage} billable` : null} testid="license-stat-overage" />
      </div>

      {s.credits.length > 0 && (
        <div className="tp-card p-5" data-testid="license-credits">
          <div className="font-display font-semibold mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-indigo-500" /> Credit packs</div>
          <div className="divide-y divide-[color:var(--bd-sub)]">
            {s.credits.map((c) => (
              <div key={c.id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-medium text-slate-800 flex-1 min-w-0 truncate">{c.name}</span>
                <span className="tabular-nums">{c.remaining} / {c.interviews} left</span>
                <span className="text-slate-500">expires {fmtDate(c.expires_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Breakdown title="By job" rows={s.by_job} testid="license-by-job" />
        <Breakdown title="By client" rows={s.by_client} testid="license-by-client" />
        <Breakdown title="By recruiter" rows={s.by_recruiter} testid="license-by-recruiter" />
      </div>

      <div className="tp-card p-5" data-testid="license-history">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold">Last 6 months</div>
          <button type="button" onClick={download} data-testid="license-export-csv" className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-500"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
        <div className="flex items-end gap-3 h-32">
          {s.history.map((h) => (
            <div key={h.period} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
              <span className="text-xs tabular-nums text-slate-600">{h.count}</span>
              <div className="w-full max-w-[48px] rounded-t-md bg-indigo-400" style={{ height: `${Math.max(3, (85 * h.count) / maxHist)}%` }} />
              <span className="text-[11px] text-slate-500">{new Date(`${h.period}-01T00:00:00`).toLocaleDateString(undefined, { month: "short" })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
